import { useSetAtom } from "jotai";
import Axios, { AxiosResponse } from 'axios';
import { environment } from "../../environment";
import { errorCatcher, snackbarSettingsAtom } from "../../hooks";
import { useLogin } from "../login";
import {
    ICustomerPortalDomainSettings,
    ICustomerPortalDomainSettingsResponse,
    ICustomerPortalLayoutSettings,
    IValidateDomainRequest,
    IValidateDomainResponse,
    IValidateDomainError,
} from "./types";

type GetDomainSettings = (brandId: number) => Promise<ICustomerPortalDomainSettingsResponse | null>;
type SaveDomainSettings = (payload: ICustomerPortalDomainSettings) => Promise<ICustomerPortalDomainSettingsResponse | null>;
type SaveLayoutSettings = (payload: ICustomerPortalLayoutSettings) => Promise<ICustomerPortalDomainSettingsResponse | null>;
type ValidateDomain = (payload: IValidateDomainRequest) => Promise<IValidateDomainResponse | IValidateDomainError | null>;
type UploadBanner = (file: File) => Promise<string | null>;
type UploadLogo = (file: File) => Promise<string | null>;

type CustomerPortalKeyFunctions = {
    getDomainSettings: GetDomainSettings;
    saveDomainSettings: SaveDomainSettings;
    saveLayoutSettings: SaveLayoutSettings;
    validateDomain: ValidateDomain;
    uploadBanner: UploadBanner;
    uploadLogo: UploadLogo;
};

type UseCustomerPortal = CustomerPortalKeyFunctions;

export const useCustomerPortal = (): UseCustomerPortal => {
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const getDomainSettings = async (brandId: number): Promise<ICustomerPortalDomainSettingsResponse | null> => {
        if (!authToken) return null;
        const getResponse: AxiosResponse<ICustomerPortalDomainSettingsResponse> | void = await Axios.get(
            `${environment.v2Url}/customer-portal/domain-settings`,
            {
                params: { brand_id: brandId },
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (getResponse) {
            return getResponse.data;
        }
        return null;
    };

    const saveDomainSettings = async (payload: ICustomerPortalDomainSettings): Promise<ICustomerPortalDomainSettingsResponse | null> => {
        if (!authToken) return null;
        console.log(payload);
        const saveResponse: AxiosResponse<ICustomerPortalDomainSettingsResponse> | void = await Axios.post(
            `${environment.v2Url}/customer-portal/domain-settings`,
            payload,
            {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (saveResponse) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Domain settings saved successfully.'
            });
            return saveResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while saving domain settings.'
            });
            return null;
        }
    };

    const saveLayoutSettings = async (payload: ICustomerPortalLayoutSettings): Promise<ICustomerPortalDomainSettingsResponse | null> => {
        if (!authToken) return null;
        const saveResponse: AxiosResponse<ICustomerPortalDomainSettingsResponse> | void = await Axios.post(
            `${environment.v2Url}/customer-portal/layout-settings`,
            payload,
            {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (saveResponse) {
            setSnackbar({
                show: true,
                snackbarLevel: 'info',
                text: 'Layout settings saved successfully.',
            });
            return saveResponse.data;
        } else {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while saving layout settings.',
            });
            return null;
        }
    };

    const uploadBanner: UploadBanner = async (file: File): Promise<string | null> => {
        if (!authToken) return null;
        try {
            const form = new FormData();
            // Use dedicated portal banner upload endpoint
            form.append('portal_banner', file);

            const uploadResponse: AxiosResponse<string> | void = await Axios.put(
                `${environment.brandsUrl}/portal-banner`,
                form,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        'X-Wallmates-Auth': authToken,
                    },
                }
            ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (uploadResponse && uploadResponse.data) {
                return uploadResponse.data;
            }

            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while uploading the banner image.',
            });
            return null;
        } catch (err: any) {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.message || 'There was an error while uploading the banner image.',
            });
            return null;
        }
    };

    const validateDomain = async (payload: IValidateDomainRequest): Promise<IValidateDomainResponse | IValidateDomainError | null> => {
        if (!authToken) return null;
        try {
            const validateResponse: AxiosResponse<IValidateDomainResponse> = await Axios.post(
                `${environment.v2Url}/customer-portal/validate-domain`,
                payload,
                {
                    headers: {
                        'X-Wallmates-Auth': authToken
                    }
                }
            );
            return validateResponse.data;
        } catch (err: any) {
            // Check if it's an error response from the API
            if (err.response && err.response.data) {
                // Return the error message and errors array from the API
                return {
                    success: false,
                    message: err.response.data.message || 'DNS validation failed.',
                    errors: err.response.data.errors || [],
                    status: err.response.status,
                } as IValidateDomainError;
            }
            // Handle other errors
            errorCatcher(err, loginFunctions.logout);
            return {
                success: false,
                message: err.message || 'There was an error while validating domain.',
                errors: [],
            } as IValidateDomainError;
        }
    };

    const uploadLogo: UploadLogo = async (file: File): Promise<string | null> => {
        if (!authToken) return null;
        try {
            const form = new FormData();
            // Use dedicated portal logo upload endpoint
            form.append('portal_logo', file);

            const uploadResponse: AxiosResponse<string> | void = await Axios.put(
                `${environment.brandsUrl}/portal-logo`,
                form,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        'X-Wallmates-Auth': authToken,
                    },
                }
            ).catch((err: any) => errorCatcher(err, loginFunctions.logout));

            if (uploadResponse && uploadResponse.data) {
                return uploadResponse.data;
            }

            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: 'There was an error while uploading the logo image.',
            });
            return null;
        } catch (err: any) {
            setSnackbar({
                show: true,
                snackbarLevel: 'error',
                text: err?.message || 'There was an error while uploading the logo image.',
            });
            return null;
        }
    };

    return {
        getDomainSettings,
        saveDomainSettings,
        saveLayoutSettings,
        uploadBanner,
        uploadLogo,
        validateDomain,
    };
};

